import type { ReactNode } from 'react';
import type { RouteObject } from 'react-router-dom';
import {
  appPath,
  LEGACY_APP_SEGMENTS,
  LEGACY_WORK_AREA_SEGMENT,
  parseAddress,
  workAreaAddressPatterns,
} from '@featit/shared/addressing/paths';
import {
  NAMESPACE_DECISIONS_SEGMENT,
  NAMESPACE_TASKS_SEGMENT,
} from '@/features/work-areas/workAreaOverviewUrls';

/**
 * R-5 as a route table (F-0250): the root belongs to namespaces, and everything internal lives
 * under `/-/`.
 *
 * ⭐ **`ROUTES` in `App.tsx` is the table the app routes on, exactly** — no transformation is applied
 * after it. The helpers here build its parts:
 *
 * - `appRoutes` puts a list of app routes under the prefix. A route is declared as it reads inside
 *   the app (`/settings`) — the same form `appPath` takes — so a lane adding a route to that list
 *   needs to know nothing about this file.
 * - `workAreaAddressRoutes` declares a work area and its routes once per namespace depth, from the
 *   same patterns the iPhone app's path configuration is generated from (AC-AD3, AC-AD10).
 * - `legacyAddressRoutes` answers the addresses from before the cutover (AC-AD4 … AC-AD6).
 *
 * A root route declared anywhere else is caught by `reservedRouteNames.test.ts` (AC-AD2).
 */

/** An app route list under `/-/`: `{ path: '/settings' }` is routed at `/-/settings`. */
export function appRoutes(routes: readonly RouteObject[]): RouteObject[] {
  return routes.map((route) =>
    typeof route.path === 'string' && route.path.startsWith('/')
      ? { ...route, path: appPath(route.path) }
      : route.children
        ? { ...route, children: appRoutes(route.children) }
        : route,
  );
}

/**
 * A work area and every route on it, once per namespace depth: `/:ns1/:workArea`, with children
 * `-/items`, `-/items/:key` …, then `/:ns1/:ns2/:workArea`, and so on to `MAX_NAMESPACE_DEPTH`.
 *
 * `children` are declared as they read inside the work area (`items/:key`, and `index` for its home);
 * the `-/` separator is added here. React Router ranks the static `-` above any parameter, so a
 * route on a work area never reads as a deeper work area.
 */
export function workAreaAddressRoutes(
  element: ReactNode,
  children: readonly RouteObject[],
): RouteObject[] {
  const separated = children.map((child) =>
    child.index || typeof child.path !== 'string' ? child : { ...child, path: `-/${child.path}` },
  );
  return workAreaAddressPatterns('').map((path) => ({
    path,
    element,
    handle: WORK_AREA_BOUNDARY_HANDLE,
    children: separated,
  }));
}

/**
 * Carried by every route `WorkAreaRouteBoundary` answers — the work-area routes and the router's
 * catch-all — so `Layout` can find that match and name the state the boundary draws
 * (`workAreaRouteState`) instead of reading a missing page's address as the work area's home.
 */
export const WORK_AREA_BOUNDARY_HANDLE = { workAreaBoundary: true } as const;

export function isWorkAreaBoundaryHandle(handle: unknown): boolean {
  return (
    typeof handle === 'object' &&
    handle !== null &&
    (handle as { workAreaBoundary?: unknown }).workAreaBoundary === true
  );
}

/**
 * The decision selected inside All work areas (I-0602): `/-/work-areas/decisions/<work area>/<key>`,
 * once per namespace depth. Declared in the app form; `appRoutes` adds the prefix.
 */
export function workAreasDecisionRoutes(element: ReactNode): RouteObject[] {
  return workAreaAddressPatterns('/:decisionKey', {
    prefix: '/work-areas/decisions',
    separated: false,
  }).map((path) => ({ path, element }));
}

/**
 * T-0565, T-0566: an item opened from a scope's Activity feed on a phone, as a pushed screen of its
 * own — `<scope's Activity>/<work area>/<key>`, addressed as a decision is (I-0602), and still inside
 * that scope. `activityPath` is the scope's feed, in the app form: `/work-areas/activity` for All
 * work areas, `/namespaces/:namespaceId/activity` for an organisation.
 */
export function activityItemRoutes(activityPath: string, element: ReactNode): RouteObject[] {
  return workAreaAddressPatterns('/:itemKey', {
    prefix: activityPath,
    separated: false,
  }).map((path) => ({ path, element }));
}

/** The task selected inside All work areas (I-0954): `/-/work-areas/tasks/<work area>/<key>`. */
export function workAreasTaskRoutes(element: ReactNode): RouteObject[] {
  return workAreaAddressPatterns('/:itemKey', {
    prefix: '/work-areas/tasks',
    separated: false,
  }).map((path) => ({ path, element }));
}

/**
 * The decision selected inside a namespace's own scope (F-0383, §10.9 NV-6), the namespace twin of
 * {@link workAreasDecisionRoutes}: `/-/namespaces/:namespaceId/decisions/<work area>/<key>`, once
 * per namespace depth the work area itself may sit at (unrelated to, and composed with, the
 * `:namespaceId` the scope is read from).
 */
export function namespaceDecisionRoutes(element: ReactNode): RouteObject[] {
  return workAreaAddressPatterns('/:decisionKey', {
    prefix: `/namespaces/:namespaceId/${NAMESPACE_DECISIONS_SEGMENT}`,
    separated: false,
  }).map((path) => ({ path, element }));
}

/** The task selected inside a namespace's own scope (F-0383, §10.9 NV-6), the namespace twin of
 * {@link workAreasTaskRoutes}. */
export function namespaceTaskRoutes(element: ReactNode): RouteObject[] {
  return workAreaAddressPatterns('/:itemKey', {
    prefix: `/namespaces/:namespaceId/${NAMESPACE_TASKS_SEGMENT}`,
    separated: false,
  }).map((path) => ({ path, element }));
}

/**
 * The addresses from before the cutover: `/p/…` and every old root app segment, each answered by
 * `element`, which replaces it with its new address.
 *
 * ⚠️ A splat ranks below a deep enough run of parameters, so `/p/acme~tracker/items/F-1` is matched by
 * a work-area route rather than by `/p/*`. That is safe by construction: `p`, `settings` and the
 * other old segments are reserved, so no real namespace starts with one, and the work-area and
 * namespace routes hand such an address to the same redirect (`isLegacyAddress`). These routes are
 * what a short old address matches, and what gives the iPhone app a tab for it.
 */
export function legacyAddressRoutes(element: ReactNode): RouteObject[] {
  return [LEGACY_WORK_AREA_SEGMENT, ...LEGACY_APP_SEGMENTS].map((segment) => ({
    path: `/${segment}/*`,
    element,
  }));
}

/** Whether a pathname is an address from before the cutover. */
export function isLegacyAddress(pathname: string): boolean {
  return parseAddress(pathname).kind === 'legacy';
}

/** The first path segment of an absolute route path, or `''` for the root route itself. */
export function firstSegmentOf(path: string): string {
  return path.replace(/^\/+/, '').split('/')[0] ?? '';
}

/** `true` for a segment React Router treats as a parameter or a wildcard. */
export function isDynamicSegment(segment: string): boolean {
  return segment.startsWith(':') || segment === '*';
}

/**
 * Every root-level route path in a table, in declaration order.
 *
 * Walks through layout routes — `RouterRoot`, `ProtectedRoute` and `Layout` have no `path` of their
 * own — so a route nested three wrappers deep is still found. A child with a *relative* path hangs
 * off its parent and is not root-level, which is why only absolute paths are collected.
 */
export function rootRoutePaths(routes: readonly RouteObject[]): string[] {
  const found: string[] = [];
  const walk = (nodes: readonly RouteObject[]): void => {
    for (const node of nodes) {
      if (typeof node.path === 'string' && node.path.startsWith('/')) {
        found.push(node.path);
        continue; // children below are relative to this route, not to the root.
      }
      if (node.children) walk(node.children);
    }
  };
  walk(routes);
  return found;
}
